import classNames from "classnames";
import { useRef, useState } from "react";
import * as React from "react";
import { Button } from "@components/Button";
import { useAnimation } from "@components/FavoriteTeamModal/_useAnimation";
import { useSizeAdjuster } from "@components/FavoriteTeamModal/_useSizeAdjuster";
import { Text } from "@components/Text";
import { FavoriteItemTypeEnum } from "@graphql/__generated__/types";
import { CommonDataState } from "@hooks/useCommonData";
import { useFavoriteTargatTeamTabs } from "@hooks/useFavoriteTargatTeamTabs";
import useIsPc from "@hooks/useIsPc";
import { FavoriteTeamModalIcon } from "./_FavoriteTeamModalIcon";
import { FavoriteTeamModalSportIcon } from "./_FavoriteTeamModalSportIcon";
import { FavoriteTeamModalTournament } from "./_FavoriteTeamModalTournament";
import { useFavoriteTeamModalSelection } from "./_useFavoriteTeamModalSelection";
import { useSwipeClose } from "./_useSwipeClose";
import styles from "./index.module.css";
import type { FavoriteItem } from "./_useFavoriteTeamModalSelection";

type FavoriteTeamModalProps = {
  onClose: () => void;
  onCloseAnimationEnd: () => void;
  onApplied: (items: FavoriteItem[]) => Promise<boolean>;
  willClose: boolean;
  initialItems: FavoriteItem[];
};

export const MAX_FAVORITES = 10;

const SPORT_TAB_INDEX = 2;

export const FavoriteTeamModal: React.FC<FavoriteTeamModalProps> = (props) => {
  const isPc = useIsPc.useContainer();
  const favoriteTeamListRef = useRef(null);
  const swipeClose = useSwipeClose({
    onClose: props.onClose,
    enabled: !isPc,
    scrollContentRef: favoriteTeamListRef,
  });
  const { animationStyle, overlayAnimationStyle, animationEndHandler } =
    useAnimation(props.willClose, props.onCloseAnimationEnd);

  const feedbackStyle = {
    "--delta-y": swipeClose.feedbackDistance + "px",
  } as React.CSSProperties;

  const {
    selectedTabIndex,
    currentTournament,
    teamsByCurrentTournament,
    categoryTabs,
    changeTournament,
  } = useFavoriteTargatTeamTabs();
  const selectedCategory = categoryTabs[selectedTabIndex];

  const { teamList, spacers, rowCount } = useSizeAdjuster(
    teamsByCurrentTournament
  );

  const { data: commonData } = CommonDataState.useContainer();
  const sportGenres = commonData?.sportGenres ?? [];

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const isSportTab = activeTabIndex === SPORT_TAB_INDEX;

  const selection = useFavoriteTeamModalSelection({
    initialItems: props.initialItems,
    maxItems: MAX_FAVORITES,
  });

  const [isSaving, setIsSaving] = useState(false);

  const saveHandler = async () => {
    setIsSaving(true);
    const result = await props.onApplied(selection.items);
    if (result) {
      props.onClose();
    }
    setIsSaving(false);
  };

  const handleTabChange = (tabIndex: number) => {
    if (tabIndex === SPORT_TAB_INDEX) {
      setActiveTabIndex(SPORT_TAB_INDEX);
      return;
    }
    setActiveTabIndex(tabIndex);
    changeTournament(
      tabIndex,
      categoryTabs[tabIndex]?.previouslySelectedTournamentId
    );
  };

  const allTabs = [
    ...categoryTabs.map((ct) => ({ label: ct.label })),
    { label: "他スポーツ" },
  ];

  return (
    <div className={styles.modalWrapper} {...swipeClose.handlers}>
      <div
        className={classNames(styles.modalOverlay, overlayAnimationStyle)}
        onClick={props.onClose}
      />
      <div className={styles.modalInner}>
        <button
          type="button"
          className={classNames(styles.modalClose, animationStyle)}
          onClick={props.onClose}
          onAnimationEnd={animationEndHandler}
        ></button>
        <div
          className={classNames(styles.modalContent, animationStyle)}
          style={feedbackStyle}
          onAnimationEnd={animationEndHandler}
        >
          <div className={classNames(styles.modalTopBar, "md:none")}></div>
          <div className={styles.favoriteTeamHeader}>
            {allTabs.map((tab, i) => (
              <div
                key={tab.label}
                className={classNames(styles.favoriteTeamHeaderItem, {
                  [styles.isActive]: activeTabIndex === i,
                })}
                onClick={() => handleTabChange(i)}
              >
                <span>{tab.label}</span>
              </div>
            ))}
          </div>
          {!isSportTab &&
            selectedCategory &&
            selectedCategory.tournamentTabs.length > 1 && (
              <div className={styles.favoriteTeamTab}>
                {selectedCategory.tournamentTabs.map((t) => (
                  <FavoriteTeamModalTournament
                    key={t.id}
                    tournament={t}
                    isSelected={t.id === currentTournament?.id}
                    onClick={() => changeTournament(activeTabIndex, t.id)}
                  />
                ))}
              </div>
            )}
          <div className={styles.favoriteTeamList} ref={favoriteTeamListRef}>
            {!isSportTab && currentTournament?.shouldShowDazn && (
              <div className={styles.copyright}>
                <img
                  className={styles.logoDAZN}
                  src="/img/logo_dazn.svg"
                  alt=""
                />
                Powered by DAZN
              </div>
            )}

            <ul className={styles.noteList}>
              <li className={styles.noteListItem}>
                <Text size={12}>
                  お気に入りは最大{MAX_FAVORITES}件まで追加できます。
                </Text>
              </li>
              <li
                className={
                  isSportTab ? styles.noteListItem : styles.noteListItemActive
                }
              >
                <Text size={12}>
                  {isSportTab
                    ? "このスポーツは、必ずしも店舗上映されるとは限りません。"
                    : "このスポーツは店舗上映に対応しています。"}
                </Text>
              </li>
            </ul>

            {isSportTab ? (
              <div
                className={classNames(
                  styles.favoriteTeamListContent,
                  styles.listContentLeft,
                  {
                    [styles.rowsSmall]: isPc && sportGenres.length <= 8,
                    [styles.rowsMedium]:
                      isPc &&
                      sportGenres.length > 8 &&
                      sportGenres.length <= 16,
                    [styles.rowsLarge]: isPc && sportGenres.length > 16,
                  }
                )}
              >
                {sportGenres.map((sport) => (
                  <FavoriteTeamModalSportIcon
                    key={sport.id}
                    sport={sport}
                    onClick={selection.clickHandler}
                    selectionIndex={selection.getSelectionIndex(
                      FavoriteItemTypeEnum.SportGenre,
                      sport.id
                    )}
                  />
                ))}
              </div>
            ) : (
              <div
                className={classNames(styles.favoriteTeamListContent, {
                  [styles.rowsSmall]: isPc && rowCount > 0 && rowCount <= 2,
                  [styles.rowsMedium]: isPc && rowCount >= 3 && rowCount <= 4,
                  [styles.rowsLarge]: isPc && rowCount >= 5,
                })}
                ref={teamList}
              >
                {teamsByCurrentTournament.map((t) => (
                  <FavoriteTeamModalIcon
                    key={t.id}
                    team={t}
                    onClick={selection.clickHandler}
                    selectionIndex={selection.getSelectionIndex(
                      FavoriteItemTypeEnum.Team,
                      t.id
                    )}
                  />
                ))}
                {spacers}
              </div>
            )}

            <div className={styles.favoriteTeamButtonBlock}>
              <Button
                className={classNames(styles.button, styles.clearButton)}
                disabled={!selection.isClearable}
                onClick={selection.clear}
                styleType="filled"
                colorType="secondary"
              >
                <Text size={15} bold>
                  クリア
                </Text>
              </Button>
              <Button
                className={classNames(styles.button, styles.addButton)}
                styleType="filled"
                colorType="primary"
                onClick={saveHandler}
                disabled={isSaving}
              >
                <Text size={15} bold>
                  保存する
                </Text>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FavoriteTeamModal;
